/**
 * AccordionSpecies.jsx — CORPORATE REBELS ACCORDION GRAMMAR
 *
 * BATCH 3A P3 — Reusable editorial accordion component.
 *
 * SPECIES PARITY V3:
 *   COLLAPSED = scarlet handwritten number + scarlet plus
 *   EXPANDED  = black handwritten number + black X + scarlet circle
 *
 *   The expanded row must feel like an already-marked editorial answer
 *   on a printed page. NOT a standard UI expand control.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExpandToggle } from '../ui/ExpandToggle';

function RebelIndex({ num, isOpen }) {
  return (
    <span
      className="inline-block min-w-[28px] text-center"
      style={{
        fontFamily: '"Permanent Marker", cursive',
        fontSize: 16,
        color: isOpen ? 'var(--text-primary, #0A0A0A)' : 'var(--g-editorial-red, #CD2200)',
        opacity: isOpen ? 0.85 : 0.75,
        transition: 'color 250ms ease',
      }}
      data-testid={`accordion-index-${num}`}
    >
      {String(num).padStart(2, '0')}
    </span>
  );
}

function AccordionItem({ item, index, isOpen, onToggle }) {
  return (
    <div
      data-testid={`accordion-item-${index}`}
      style={{
        borderBottom: '1px solid var(--border-soft, rgba(15,23,42,0.08))',
      }}
    >
      {/* HEADER */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 py-5 px-1 text-left transition-colors group"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        data-testid={`accordion-trigger-${index}`}
      >
        <RebelIndex num={index + 1} isOpen={isOpen} />
        <span
          className="flex-1 text-[15px] sm:text-[16px] font-medium leading-snug tracking-tight"
          style={{
            color: isOpen ? 'var(--text-primary, #0A0A0A)' : 'var(--text-secondary, #555555)',
            fontWeight: isOpen ? 600 : 500,
            transition: 'color 200ms, font-weight 200ms',
          }}
        >
          {item.question}
        </span>
        <ExpandToggle open={isOpen} size="sm" testId={`accordion-expand-icon-${index}`} />
      </button>

      {/* BODY — editorial answer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div
              className="pb-6 pl-[44px] pr-4"
              style={{ maxWidth: 580 }}
            >
              <p
                className="text-[13px] sm:text-[14px] leading-[1.7]"
                style={{ color: 'var(--text-secondary, #555555)', whiteSpace: 'pre-line' }}
              >
                {item.answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function AccordionSpecies({ items = [], defaultOpen = null, className = '' }) {
  const [openIndex, setOpenIndex] = useState(defaultOpen);

  return (
    <div className={className} data-testid="accordion-species">
      {/* Top separator */}
      <div style={{ height: 1, background: 'var(--border-soft, rgba(15,23,42,0.08))' }} />

      {items.map((item, i) => (
        <AccordionItem
          key={i}
          item={item}
          index={i}
          isOpen={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? null : i)}
        />
      ))}
    </div>
  );
}

export default AccordionSpecies;
